import EventEmitter from "src/modules/Events";
import debug from "src/modules/debug-logger";
import Presence from "src/constants/presence";
import * as myJid from "./jid";
import { PingService } from "./PingService";
import { xml, jid } from "@xmpp/client";
import initializeXmppClient from "./initializeXmppClient";
import { decodeBase64 } from "src/modules/protocol-buffers";
import {
  prepareEnvelope,
  createMultiParts,
  MAX_BYTE_LENGTH,
  createEnvelopeBuffer,
  decodeEnvelope,
  getEnvelopeContent,
  concatDataParts
} from "src/modules/communication-protobuf";

const log = window.require("electron-log");

export default class Messenger extends EventEmitter {
  peers = {};
  vCardRequests = {};
  presence = Presence.UNAVAILABLE;
  pingService = null;

  async initiate({ xmppCredentials }) {
    this.xmppLogin = xmppCredentials.xmppLogin;
    this.xmppPassword = xmppCredentials.xmppPassword;
    this.xmpp = initializeXmppClient({
      messenger: this
    });
    await this.xmpp.start();

    this.pingService = new PingService();

    this.pingService.initialize({ xmpp: this.xmpp });
  }

  sendByXmpp = async (message) => {
    if (this.xmpp?.status === "online") {
      return await this.xmpp.send(message);
    } else {
      debug.messenger(
        `xmpp.status === ${this.xmpp.status}, message won't be send`,
        message
      );
      return false;
    }
  };

  setPresence(status = this.presence, payload = {}) {
    this.presence = status;
    this.presencePayload = payload;

    this.emit("presence-updated", status);

    this.sendByXmpp(
      xml(
        "presence",
        {},
        xml("status", {}, status),
        xml("payload", { xmlns: "ttmonitor" }, JSON.stringify(payload)),
        xml("priority", {}, 99)
      )
    );
  }
  updatePresencePayload(payload) {
    this.setPresence(this.presence, payload);
  }

  processPresenceStanza(stanza) {
    const { from, type } = stanza.attrs;
    const xmppLogin = jid(from).local;
    let status = stanza.getChildText("status");
    let payload = stanza.getChildText("payload");
    if (type === "unavailable" || status == null) {
      status = "unavailable";
    }
    debug.messenger(
      `Received Presence Stanza, status: ${status}, payload: ${payload} from: ${xmppLogin}`
    );

    this.emit("received-presence", {
      xmppLogin,
      status,
      payload: payload ? JSON.parse(payload) : {}
    });
  }

  getDataChannelForPayload(payload, aPeer) {
    const { audioDataChannel, commDataChannel, peerConnection } = aPeer;
    const { connectionState } = peerConnection;

    const isAudioPacket = payload.message && payload.message.audioPacket;
    const dataChannel = isAudioPacket ? audioDataChannel : commDataChannel;

    if (connectionState === "connected" && dataChannel.readyState === "open") {
      return dataChannel;
    }
    return null;
  }

  getDeviceName(xmppLogin) {
    if (this.vCardRequests[xmppLogin]) {
      return this.vCardRequests[xmppLogin].promise;
    }
    const vCardPromise = new Promise(async (resolve, reject) => {
      this.vCardRequests[xmppLogin] = {
        resolve,
        reject
      };

      const xmppMessage = xml(
        "iq",
        {
          to: myJid.addDomainTo(xmppLogin),
          id: "v3",
          type: "get"
        },
        xml("vCard", { xmlns: "vcard-temp" })
      );
      setTimeout(() => {
        if (this.vCardRequests[xmppLogin]) {
          reject();
          delete this.vCardRequests[xmppLogin];
        }
      }, 5000);
      await this.sendByXmpp(xmppMessage);
    });
    this.vCardRequests[xmppLogin].promise = vCardPromise;
    return vCardPromise;
  }
  processVcard({ xmppLogin, vCard, error }) {
    const vCardRequest = this.vCardRequests[xmppLogin];
    if (vCardRequest == null) return;
    const deviceName = vCard.getChildText("NICKNAME") || "";
    error ? vCardRequest.reject() : vCardRequest.resolve(deviceName);
  }

  async sendAsEnvelope({ to: xmppLogin, payload, sendBy }) {
    const peer = this.peers[xmppLogin];
    let dataChannel = null;
    if (!sendBy) {
      dataChannel = peer ? this.getDataChannelForPayload(payload, peer) : null;
      sendBy = dataChannel == null ? "xmpp" : "dataChannel";
    }

    const envelope = prepareEnvelope({ payload, sendBy });

    if (sendBy === "xmpp") {
      const to = myJid.getFullJid(xmppLogin);
      const xmppMessage = xml(
        "message",
        { to, type: "chat" },
        xml("subject", {}, "pb"),
        xml("body", {}, envelope)
      );
      this.sendByXmpp(xmppMessage);
    } else if (sendBy === "dataChannel") {
      const byteLength = Buffer.byteLength(envelope);
      debug.dataChannel("Envelope byte length:", byteLength);

      if (byteLength < MAX_BYTE_LENGTH) {
        debug.dataChannel("Sending over dataChannel");
        dataChannel.send(envelope);
      } else {
        debug.dataChannel("Sending over dataChannel as MultiParts");

        const multiParts = createMultiParts(envelope);
        debug.dataChannel("MultiParts created", multiParts);

        multiParts.forEach((multipart) => {
          debug.dataChannel("About to create MultiPart Envelope", multipart);

          const envelopeBuffer = createEnvelopeBuffer({ multipart });

          debug.dataChannel("About to send MultiPart Envelope", envelopeBuffer);

          dataChannel.send(envelopeBuffer);
        });
      }
    }
  }
  sendPushNotification(xmppLogin, notification) {
    const to = myJid.getFullJid(xmppLogin);
    const xmppMessage = xml(
      "message",
      { to, type: "chat" },
      xml("subject", {}, "pushnotification"),
      xml("body", {}, notification)
    );
    this.sendByXmpp(xmppMessage);
  }
  processMessageStanza(stanza) {
    try {
      const subject = stanza.getChildText("subject");
      if (subject !== "pb") {
        return;
      }
      const { from, type: messageType } = stanza.attrs;
      const xmppLogin = jid(from).local;

      const base64EncodedEnvelope = stanza.getChildText("body");
      const bufferEnvelope = decodeBase64(base64EncodedEnvelope);
      const decodedEnvelope = decodeEnvelope(bufferEnvelope);
      const { type, payload } = getEnvelopeContent(decodedEnvelope);

      if (messageType === "error") {
        debug.xmpp(
          `Received error message (${type}) from ${xmppLogin}, message will be ignored`,
          stanza,
          payload
        );
        return;
      }
      this.emit(type, payload, xmppLogin);
    } catch (err) {
      log.error(err);
    }
  }

  createDataChannelMessageHandler = (aPeer) => (e) => {
    debug.dataChannel("dataChannel.onmessage fired:", e);
    if (aPeer == null) return;
    const { jid } = aPeer;

    const envelopeAsArrayBuffer = e.data;
    const envelopeAsBuffer = Buffer.from(envelopeAsArrayBuffer);
    const envelope = decodeEnvelope(envelopeAsBuffer);

    if (envelope.multipart) {
      this.processMultiPart(envelope.multipart, jid);
      return;
    }

    const { type, payload } = getEnvelopeContent(envelope);

    this.emit(type, payload, jid);
  };
  addPeer(aPeer) {
    this.peers[aPeer.jid] = aPeer;
    const onMessageHandler = this.createDataChannelMessageHandler(aPeer);
    aPeer.commDataChannel.onmessage = onMessageHandler;
    aPeer.audioDataChannel.onmessage = onMessageHandler;
  }
  removePeer(peerXmppLogin) {
    this.peers[peerXmppLogin] = null;
    delete this.peers[peerXmppLogin];
  }
  processMultiPart(newMultiPart, xmppLogin) {
    debug.dataChannel("Received multiPart", newMultiPart);

    const { uuid, total, part, data } = newMultiPart;
    let multiPart = this.peers[xmppLogin][uuid];

    debug.dataChannel("Current multiPart", multiPart);

    if (multiPart == null) {
      multiPart = {
        total,
        uuid,
        dataParts: []
      };
      this.peers[xmppLogin][uuid] = multiPart;
    }

    const expectedPartIndex = multiPart.dataParts.length;
    if (expectedPartIndex !== part) {
      this.peers[xmppLogin].multiPart = null;
      return;
    }

    multiPart.dataParts.push(Buffer.from(data));

    if (part === total - 1) {
      debug.dataChannel("Received last multipart:", multiPart);

      const envelopeBuffer = concatDataParts(multiPart.dataParts);
      const envelope = decodeEnvelope(envelopeBuffer);
      const { type, payload } = getEnvelopeContent(envelope);

      this.peers[xmppLogin][uuid] = null;

      this.emit(type, payload, xmppLogin);
    }
  }

  fetchRoster = async () => {
    const roster = await this.xmpp.getRoster();
    for (let device of roster) {
      if (!device.name) {
        device.name = await this.getDeviceName(device.jid);
      }
    }
    debug.messenger("Received roster:", roster);
    this.emit("received-roster", roster);
    return roster;
  };

  async stop() {
    if (this.pingService) this.pingService.stop();
    if (this.xmpp) await this.xmpp.stop();
    this.vCardRequests = {};
  }
  async restart() {
    if (this.pingService) this.pingService.stop();
    if (this.xmpp) await this.xmpp.stop();

    this.xmpp = initializeXmppClient({ messenger: this });
    await this.xmpp.start();
    this.setPresence(this.presence, this.presencePayload);

    if (this.pingService) {
      this.pingService.initialize({ xmpp: this.xmpp });
    }
  }
}
